import type { Entry } from '../../../../features/admin/modules/modules.model';
import type { AutocompleteOption } from '../autocomplete.types';

/**
 * Where a suggestion in the addressbook field came from.
 *
 * The FE has to carry this itself: `AddressSearchResult` (src/app/api/models) is generated from
 * the OpenAPI spec and has no origin field. Everything under src/app/api/ is regenerated
 * wholesale from the backend spec, so a field added there would be gone after the next run.
 */
export const AddressbookOptionSourceEnum = <const>{
  Book: 'book',
  Maps: 'maps',
};
export type AddressbookOptionSource = (typeof AddressbookOptionSourceEnum)[keyof typeof AddressbookOptionSourceEnum];

/**
 * One row in the addressbook field's suggestion list.
 *
 * Extends the shared autocomplete row so the inherited option plumbing keeps working, and narrows
 * `value` to the addressbook-shaped `Entry` this field always stores — never a bare string.
 *
 * `source` is OPTIONAL, and that is not sloppiness: only `AddressbookSearchService` stamps it,
 * while three inherited paths write raw module options straight into the same list (hydrating a
 * saved value, the dynamic-options callback, the merge inside validation). Declaring it required
 * would describe a list that does not exist and push the mismatch into a cast. Rows without a
 * source are addressbook rows, which is why the component tests for Maps rather than for Book.
 */
export interface AddressbookOption extends AutocompleteOption {
  label: string;
  value: Entry;
  source?: AddressbookOptionSource;
}

/**
 * A suggestion the user picked but has not saved to the addressbook yet: an address-shaped Entry
 * without an id.
 *
 * This one predicate is the gate for two things at once — the "add to addressbook" link, and the
 * exemption that keeps such a value from being validated against the module it is not in yet. So
 * "any object with `.entry` and no `.id`" is too generous: any entry-shaped value that happens to
 * be unsaved would take the exemption and never be checked. Requiring the composite `address`
 * sub-object narrows it to what this field actually produces.
 */
export function isUnsavedAddressbookEntry(value: unknown): value is Entry {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const entry = value as Entry;
  return !entry.id && !!entry.entry && typeof entry.entry['address'] === 'object' && entry.entry['address'] !== null;
}
